// Plan 23b Task 3.4 — Awards Card primitive.
// Behavior contract: docs/design/components.md §20 lines 648–663.
//
// Visual structure:
//   Card div `ui-awards-card` — centered panel, width ~480px
//     ├── Title div `ui-awards-card__title` — "🏆 PUMMEL AWARDS 🏆"
//     ├── Category div `ui-awards-card__category` — category name ALL-CAPS
//     ├── Spotlight div `ui-awards-card__spotlight` — CSS radial-gradient behind winner
//     ├── Winner div `ui-awards-card__winner` — winner name
//     └── Badge div `ui-awards-card__badge` — "+1 🏆"
//
// Animation on reveal():
//   Spotlight: scale 0→1, opacity 0→1 over 0.6s back.out(1.7) at position 0
//   Winner fade-in: opacity 0→1 over 0.2s at position 0.2 (200ms after spotlight)
//
// All values from docs/design/tokens.json.

import gsap from 'gsap';
import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AwardsCardOpts {
  // no required fields — reserved for future awards-card-specific config
}

export interface RevealOpts {
  /** Award category name, e.g. 'MOST KEYS GAINED' */
  category: string;
  /** Winner player name, e.g. 'Alice' */
  winnerName: string;
}

export interface AwardsCardHandle extends UIComponentHandle {
  /** Animate the card to show a new award category + winner. */
  reveal(opts: RevealOpts): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — all values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** typography.family.headline */
const FONT_HEADLINE = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.h2 = 36px — title */
const FONT_SIZE_H2 = '36px';

/** typography.size.h3 = 28px — winner */
const FONT_SIZE_H3 = '28px';

/** typography.size.h4 = 22px — category */
const FONT_SIZE_H4 = '22px';

/** color.placement.first = #FFD700 — title color */
const COLOR_GOLD = '#FFD700';

/** color.text.primary = #FFFFFF — category + winner text */
const COLOR_TEXT_PRIMARY = '#FFFFFF';

/** color.hud.trophy-gold = #FFD700 — badge text */
const COLOR_TROPHY_GOLD = '#FFD700';

/** color.ui.panel-bg-alpha = rgba(30,35,50,0.82) — card background */
const COLOR_PANEL_BG_ALPHA = 'rgba(30, 35, 50, 0.82)';

/** color.world.sun-glow = #FFF5D0 — spotlight radial gradient center */
const COLOR_SUN_GLOW = '#FFF5D0';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** shadow.panel = 0 4px 12px rgba(0,0,0,0.50) */
const SHADOW_PANEL = '0 4px 12px rgba(0,0,0,0.50)';

/** spacing.lg = 24px */
const SPACING_LG = '24px';

/** typography.shadow.heading = "0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.60)" */
const TEXT_SHADOW_HEADING = '0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.60)';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createAwardsCard(config: UIComponentConfig & AwardsCardOpts): AwardsCardHandle {
  const { gsapRegistry, mount } = config;

  let disposed = false;
  let currentHandle: GsapTimelineHandle | null = null;

  // ── Build DOM ────────────────────────────────────────────────────────────

  // Root card element
  const el = document.createElement('div');
  el.className = 'ui-awards-card';
  el.style.cssText = [
    'position: relative',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'justify-content: center',
    'gap: 12px',
    `width: 480px`,
    `background-color: ${COLOR_PANEL_BG_ALPHA}`,
    `border-radius: ${RADIUS_LG}`,
    `box-shadow: ${SHADOW_PANEL}`,
    `padding: ${SPACING_LG}`,
    'text-align: center',
    'overflow: hidden',
  ].join('; ');

  // Title: "🏆 PUMMEL AWARDS 🏆"
  const titleEl = document.createElement('div');
  titleEl.className = 'ui-awards-card__title';
  titleEl.textContent = '🏆 PUMMEL AWARDS 🏆';
  titleEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_H2}`,
    `color: ${COLOR_GOLD}`,
    `text-shadow: ${TEXT_SHADOW_HEADING}`,
    'font-weight: 900',
    'letter-spacing: 1px',
    'margin: 0',
  ].join('; ');

  // Category
  const categoryEl = document.createElement('div');
  categoryEl.className = 'ui-awards-card__category';
  categoryEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_H4}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    `text-shadow: ${TEXT_SHADOW_HEADING}`,
    'font-weight: 700',
    'letter-spacing: 2px',
    'text-transform: uppercase',
    'margin: 0',
  ].join('; ');

  // Spotlight — CSS radial-gradient div, absolute, behind winner (z-index 0)
  const spotlightEl = document.createElement('div');
  spotlightEl.className = 'ui-awards-card__spotlight';
  spotlightEl.style.cssText = [
    'position: absolute',
    `background: radial-gradient(circle, ${COLOR_SUN_GLOW} 0%, transparent 70%)`,
    'width: 300px',
    'height: 300px',
    'border-radius: 50%',
    'pointer-events: none',
    'z-index: 0',
    'top: 50%',
    'left: 50%',
    'transform: translate(-50%, -50%)',
  ].join('; ');

  // Winner name — sits above spotlight (z-index 1)
  const winnerEl = document.createElement('div');
  winnerEl.className = 'ui-awards-card__winner';
  winnerEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_H3}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    `text-shadow: ${TEXT_SHADOW_HEADING}`,
    'font-weight: 900',
    'margin: 0',
    'position: relative',
    'z-index: 1',
  ].join('; ');

  // Badge: "+1 🏆"
  const badgeEl = document.createElement('div');
  badgeEl.className = 'ui-awards-card__badge';
  badgeEl.textContent = '+1 🏆';
  badgeEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 18px',
    `color: ${COLOR_TROPHY_GOLD}`,
    `text-shadow: ${TEXT_SHADOW_HEADING}`,
    'font-weight: 700',
    'position: relative',
    'z-index: 1',
    'margin: 0',
  ].join('; ');

  el.appendChild(titleEl);
  el.appendChild(categoryEl);
  el.appendChild(spotlightEl);
  el.appendChild(winnerEl);
  el.appendChild(badgeEl);

  // ── Helpers ───────────────────────────────────────────────────────────────

  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  // ── reveal() ──────────────────────────────────────────────────────────────

  function reveal(opts: RevealOpts): void {
    if (disposed) return;

    // 1. Update text content
    categoryEl.textContent = opts.category;
    winnerEl.textContent = opts.winnerName;

    // 2. Kill any prior animation handle
    killCurrent();

    // 3. Reset spotlight + winner to initial state before animating
    //    gsap.set initialises GSAP's internal cache so fromTo starts correctly
    gsap.set(spotlightEl, { scale: 0, opacity: 0 });
    gsap.set(winnerEl, { opacity: 0 });

    // 4. Create new timeline handle via registry
    const handle = gsapRegistry.create('awards-card:reveal');
    const tl = handle.timeline;

    // Spotlight: scale 0→1, opacity 0→1, 0.6s back.out(1.7) at position 0
    // F20a-CLASS-1: explicit numeric position as THIRD argument
    tl.fromTo(
      spotlightEl,
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(1.7)' },
      0, // explicit position — F20a-CLASS-1
    );

    // Winner fade-in: opacity 0→1, 0.2s at position 0.2 (200ms after spotlight)
    // F20a-CLASS-1: explicit numeric position as THIRD argument
    tl.fromTo(
      winnerEl,
      { opacity: 0 },
      { opacity: 1, duration: 0.2 },
      0.2, // explicit position — F20a-CLASS-1
    );

    currentHandle = handle;
  }

  // ── dispose() ────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill any in-flight animation handle
    killCurrent();

    // Detach from DOM if mounted
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }

  // ── Optional mount at construction time ──────────────────────────────────

  if (mount && !mount.contains(el)) {
    mount.appendChild(el);
  }

  return {
    element: el,
    reveal,
    dispose,
  };
}
